import React, { useState } from 'react';
import { Box, IconButton, Button, Typography, Slide, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import { usePWAInstall } from '../../contexts/PWAInstallContext';

const DISMISSED_KEY = 'pwa-install-dismissed';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const InstallPrompt = () => {
  const theme = useTheme();
  // The navbar "Install App" button is visible on md+. To avoid two install
  // options at once, the banner only renders on smaller screens.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { canInstall, promptInstall } = usePWAInstall();

  const [dismissed, setDismissed] = useState(() => {
    try {
      const t = localStorage.getItem(DISMISSED_KEY);
      return t ? Date.now() - parseInt(t, 10) < ONE_DAY_MS : false;
    } catch (_) {
      return false;
    }
  });

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, Date.now().toString());
    } catch (_) {}
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted' || outcome === 'instructions') {
      setDismissed(true);
    }
  };

  // Never show when already installed, when the navbar option is visible,
  // or when the user dismissed it recently.
  const show = canInstall && !isDesktop && !dismissed;

  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          // Sit just above the fixed BottomNavBar (~66px) without covering it.
          bottom: 'calc(66px + env(safe-area-inset-bottom, 0px) + 8px)',
          left: 8,
          right: 8,
          zIndex: 1200,
          mx: 'auto',
          maxWidth: 420,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 999,
          color: '#fff',
          background: 'linear-gradient(135deg, #323232 0%, #1a1a1a 100%)',
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
        }}
      >
        <GetAppIcon fontSize="small" sx={{ flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Install UlcaApp
        </Typography>
        <Button
          size="small"
          variant="contained"
          onClick={handleInstall}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            borderRadius: 999,
            px: 1.75,
            backgroundColor: '#1976d2',
            '&:hover': { backgroundColor: '#1565c0' },
          }}
        >
          Install
        </Button>
        <IconButton
          size="small"
          aria-label="dismiss install banner"
          onClick={handleDismiss}
          sx={{ flexShrink: 0, color: 'rgba(255, 255, 255, 0.8)', '&:hover': { color: '#fff' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Slide>
  );
};

export default InstallPrompt;
